import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

interface Event {
  label: string;
  filter: Filter[];
  customData?: string[];
}

interface Filter {
  type: string;
  value: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private socket!: WebSocket;
  private events: Event[] = [];
  private persistentEvents: Event[] = [];
  private messageSubject: Subject<Record<string, unknown>> = new Subject<Record<string, unknown>>();
  private webSocketStateSubject: Subject<number> = new Subject<number>();
  private routeChangeSubscription!: Subscription;
  private isRetrying = false;

  constructor(private router: Router) {
    this.initializeSocket();
    this.handleRouteChanges();
  }

  private initializeSocket(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.isRetrying = false;
    this.socket = new WebSocket(environment.websocketURL);
    this.initializeHandlers();
  }

  private initializeHandlers() {
    this.socket.addEventListener('open', () => {
      this.webSocketStateSubject.next(WebSocket.OPEN);
      this.sendSubscription();
    });

    this.socket.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(event.data);
        this.messageSubject.next(message);
      } catch {
        // Ignore malformed WebSocket messages
      }
    });

    this.socket.addEventListener('error', () => {
      this.webSocketStateSubject.next(WebSocket.CLOSED);
      this.retryConnection();
    });

    this.socket.addEventListener('close', () => {
      this.webSocketStateSubject.next(WebSocket.CLOSED);
      this.retryConnection();
    });
  }

  private retryConnection() {
    if (this.isRetrying) return;
    this.isRetrying = true;
    setTimeout(() => {
      if (this.socket.readyState !== WebSocket.OPEN && this.socket.readyState !== WebSocket.CONNECTING) {
        this.initializeSocket();
      }
    }, 5000); // retry every 5 seconds
  }

  public openWebsocket(): void {
    if (this.socket.readyState !== WebSocket.OPEN && this.socket.readyState !== WebSocket.CONNECTING) {
      this.initializeSocket();
    }
  }

  public onMessage(): Observable<Record<string, unknown>> {
    return this.messageSubject.asObservable();
  }

  // Sends the complete desired subscription state (page-scoped + persistent)
  // as one message that fully replaces the server's list. Used whenever a
  // filter needs to be REMOVED or CHANGED for an existing label (navigating
  // away, or an employee switching persistent filters) - merge has no
  // removal equivalent, so this is the only way to shrink/replace the
  // server-side list. A no-op while CONNECTING: the 'open' handler sends the
  // current state as soon as the socket connects, so nothing gets lost.
  private sendSubscription(): void {
    if (this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    if (this.events.length === 0 && this.persistentEvents.length === 0) {
      return;
    }
    const message = {
      action: 'subscribe',
      message: { events: [...this.events, ...this.persistentEvents] }
    };
    this.socket.send(JSON.stringify(message));
  }

  // Adds events to the server's existing subscription without touching
  // anything already subscribed (e.g. persistent events, or another page's
  // events mid-transition) - used when entering a page. Nothing needs
  // resending here because nothing is being removed. A no-op while
  // CONNECTING: the 'open' handler's full sendSubscription() already covers
  // these events once the socket connects.
  private sendMergeSubscription(events: Event[]): void {
    if (this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    if (events.length === 0) {
      return;
    }
    const message = {
      action: 'subscribe',
      message: { merge: true, events }
    };
    this.socket.send(JSON.stringify(message));
  }

  public createEvent(label: string, filter?: Filter[], customData?: string[]): void {
    const normalizedFilter = filter || [];
    const alreadyExists = this.events.some(e =>
      e.label === label && JSON.stringify(e.filter) === JSON.stringify(normalizedFilter)
    );
    if (alreadyExists) {
      return;
    }
    this.events.push({ label, filter: normalizedFilter, customData });
  }

  // Removes a single page-scoped event (matched by label + filter) without
  // touching anything else - the modal-scoped equivalent of
  // cleanupOnNavigation(), for callers (e.g. a modal opened on top of a
  // page) that added an event via merge and need to retract just that one
  // on close, leaving the host page's own subscription intact.
  public removeEvent(label: string, filter?: Filter[]): void {
    const normalizedFilter = filter || [];
    const before = this.events.length;
    this.events = this.events.filter(e =>
      !(e.label === label && JSON.stringify(e.filter) === JSON.stringify(normalizedFilter))
    );
    if (this.events.length === before) {
      return;
    }
    if (this.events.length === 0 && this.persistentEvents.length === 0) {
      this.unsubscribeFromWebsocket();
    } else {
      this.sendSubscription();
    }
  }

  public registerPersistentEvent(label: string, filter: Filter[], customData?: string[]): void {
    this.persistentEvents = this.persistentEvents.filter(e => e.label !== label);
    this.persistentEvents.push({ label, filter: filter || [], customData });
  }

  public subscribeToWebsocket() {
    this.sendMergeSubscription(this.events);
  }

  public subscribePersistentEvents() {
    this.sendSubscription();
  }

  public clearAllEvents(): void {
    this.events = [];
    this.persistentEvents = [];
  }

  private unsubscribeFromWebsocket() {
    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ action: 'unsubscribe' }));
    }
  }

  public onWebSocketStateChange(): Observable<number> {
    return this.webSocketStateSubject.asObservable();
  }

  public clearEvents() {
    this.events = [];
  }

  private handleRouteChanges() {
    this.routeChangeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.cleanupOnNavigation();
      }
    });
  }

  public closeWebsocket() {
    if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
      this.socket.close();
    }
  }

  public cleanupOnNavigation() {
    const hadPageEvents = this.events.length > 0;
    this.clearEvents();
    if (!hadPageEvents) {
      return;
    }
    // The page we're leaving had page-scoped events (e.g. per-row filters)
    // subscribed server-side - replace the subscription with what's left
    // (persistent events only) so they don't stay subscribed indefinitely.
    // Falls back to a full unsubscribe if nothing is left at all, since
    // sendSubscription() only ever sends a non-empty desired state.
    if (this.persistentEvents.length === 0) {
      this.unsubscribeFromWebsocket();
    } else {
      this.sendSubscription();
    }
  }

  ngOnDestroy() {
    this.clearEvents();
    this.unsubscribeFromWebsocket();
    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
    }
  }
}